import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

export default function LegalLayout({ title, effectiveDate, lastUpdated, tableOfContents = [], children, testid }) {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={testid}>
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo variant="wordmark" size={32} to="/" testId="legal-logo" />
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 font-medium" data-testid="legal-back">
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="text-overline text-primary mb-3">Legal</div>
        <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="legal-title">{title}</h1>
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs sm:text-sm text-muted-foreground">
          {effectiveDate && <div><span className="font-semibold text-foreground">Effective:</span> {effectiveDate}</div>}
          {lastUpdated && (<>
            <div className="h-3 w-px bg-border" />
            <div><span className="font-semibold text-foreground">Last updated:</span> {lastUpdated}</div>
          </>)}
        </div>

        <div className="mt-10 sm:mt-12 grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12">
          {tableOfContents.length > 0 && (
            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-8 bg-card border border-border rounded-2xl p-5">
                <div className="text-overline text-muted-foreground mb-4">Contents</div>
                <nav className="space-y-1.5">
                  {tableOfContents.map((item, i) => (
                    <a key={item.id} href={`#${item.id}`} className="block text-xs sm:text-sm text-muted-foreground hover:text-primary leading-snug py-1" data-testid={`toc-${item.id}`}>
                      <span className="font-mono text-[10px] text-muted-foreground/70 mr-2">{String(i + 1).padStart(2, "0")}</span>{item.label}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>
          )}
          <article className={tableOfContents.length > 0 ? "lg:col-span-3" : "lg:col-span-4"}>
            <div className="prose-orixa space-y-8">{children}</div>
          </article>
        </div>

        <div className="mt-16 pt-8 border-t border-border text-xs sm:text-sm text-muted-foreground leading-relaxed">
          For inquiries, please contact ORIXA Intelligence via{" "}
          <Link to="/book" className="text-primary font-semibold underline underline-offset-4">/book</Link>{" "}
          or through the contact channels published on your organisation&apos;s workspace.
        </div>
      </section>

      <footer className="border-t border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-xs text-muted-foreground flex flex-col sm:flex-row justify-between gap-2">
          <div>Copyright &copy; {new Date().getFullYear()} ORIXA Intelligence</div>
          <div className="flex gap-4">
            <Link to="/legal/terms" className="hover:text-foreground" data-testid="footer-legal-terms">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-foreground" data-testid="footer-legal-privacy">Privacy</Link>
            <Link to="/legal/refund" className="hover:text-foreground" data-testid="footer-legal-refund">Refund</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function Section({ id, number, title, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`section-${id}`}>
      <div className="flex items-baseline gap-3 mb-3">
        <span className="font-mono text-xs font-bold text-primary">{number}</span>
        <h2 className="font-display text-xl sm:text-2xl tracking-tight">{title}</h2>
      </div>
      <div className="text-sm sm:text-base text-foreground/85 leading-relaxed space-y-3 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-1.5 [&>ul]:text-foreground/80 [&>p]:leading-relaxed">
        {children}
      </div>
    </section>
  );
}
